/**
 * Стандартна структура відповіді від backend API
 */
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: any;
}

/**
 * Витягує дані з стандартної відповіді API
 * Backend завжди повертає { success, data, message? }
 * Axios обгортає це в response.data
 *
 * @param response - Axios response або вже розпакований об'єкт
 * @returns Дані з поля data
 */
export function unwrapApiResponse<T>(response: any): T {
  // Якщо це axios response - береме response.data
  const payload = response.data || response;

  // Витягуємо data з стандартної структури { success, data, message }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data;
  }

  // Якщо структура нестандартна - повертаємо як є
  return payload;
}

/**
 * Клас для кастомних помилок API
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Дістає повідомлення з помилки для показу користувачу. Якщо це `ApiError`/Error
 * з осмисленим message — повертає його (бек уже локалізує через i18n). Інакше — fallback.
 *
 * Використовувати в catch-блоках замість `toastService.error('generic')`:
 *   try { ... } catch (e) { toastService.error(getErrorMessage(e, 'fallback')); }
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
